import React from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  designReadyGameIds,
  getLevelById,
  isSoppelLevel,
} from '../../data/loadData';
import type { GameId } from '../../data/schemas';
import { hasPluss } from '../../store/entitlements';
import { LevelRunnerShell } from '../../games/core/levelRunner/LevelRunnerShell';
import { ParentGate } from '../../features/parentGate/ParentGate';
import { SoppelbilenGame } from '../../games/soppelbilen/SoppelbilenGame';
import { useProfileStore } from '../../store/profile';
import { useSessionStore, shouldRest } from '../../store/session';
import { settingsFor } from '../../store/ageSettings';

/** After the reward: the level list, or the garage when the session is long enough. */
function exitPath(gameId: string): string {
  const profile = useProfileStore.getState().profile;
  const { levelsDone } = useSessionStore.getState();
  const limit = settingsFor(profile?.ageBand).levelsPerSession;
  return shouldRest(levelsDone, limit, profile?.pauseReminder !== false)
    ? '/garasje'
    : `/spill/${gameId}`;
}

function isGameId(value: string | undefined): value is GameId {
  return Boolean(value && (designReadyGameIds as string[]).includes(value));
}

export const LevelPlayPage: React.FC = () => {
  const { gameId, levelId } = useParams<{ gameId: string; levelId: string }>();
  const navigate = useNavigate();
  const [gateOpen, setGateOpen] = React.useState(false);

  if (!isGameId(gameId) || !levelId) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#8ecae6]">
        <Link to="/" className="rounded-2xl bg-white px-6 py-4 font-bold">
          Hjem
        </Link>
      </div>
    );
  }

  const level = getLevelById(gameId, levelId);
  if (!level) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-[#8ecae6]">
        <p className="text-white font-display text-2xl">Nivå ikke funnet</p>
        <Link to={`/spill/${gameId}`} className="rounded-2xl bg-white px-6 py-4 font-bold">
          Tilbake
        </Link>
      </div>
    );
  }

  if (level.tier === 'pluss' && !hasPluss()) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-[#8ecae6] p-6">
        <p className="text-white font-display text-2xl text-center">Dette nivået er låst</p>
        <button
          type="button"
          className="min-h-[96px] rounded-2xl bg-white px-8 font-display text-xl font-bold"
          onClick={() => setGateOpen(true)}
        >
          For voksne
        </button>
        <Link to={`/spill/${gameId}`} className="text-white underline font-body">
          Tilbake
        </Link>
        <ParentGate open={gateOpen} onClose={() => setGateOpen(false)} />
      </div>
    );
  }

  return (
    <LevelRunnerShell
      level={level}
      gameId={gameId}
      onExit={() => navigate(exitPath(gameId))}
      onHome={() => navigate('/')}
    >
      {(api) => {
        // Søppelbilen must always mount the R3F play surface — never the Phase 0
        // "Ferdig (demo)" shortcut that jumps straight to the reward screen.
        if (gameId === 'soppelbilen') {
          if (!isSoppelLevel(level)) {
            return (
              <div className="flex h-full min-h-[100dvh] flex-col items-center justify-center gap-4 p-6">
                <p className="font-display text-2xl text-white text-center">
                  Kunne ikke laste Søppelbilen-nivået
                </p>
              </div>
            );
          }
          return <SoppelbilenGame level={level} api={api} />;
        }
        // Phase 2/3 placeholders: keep demo complete so design-ready games stay exercisable.
        return (
          <div className="flex h-full min-h-screen flex-col items-center justify-center gap-6 p-6">
            <p className="font-display text-2xl text-white text-center">
              {gameId === 'snobroyteren' ? 'Snøbrøyteren kommer i fase 2' : 'Brannbilen kommer i fase 3'}
            </p>
            <button
              type="button"
              className="min-h-[96px] rounded-full bg-white px-8 font-display text-2xl font-bold text-[#27A9E1]"
              onClick={() => {
                api.pokeActivity();
                api.completeLevel();
              }}
            >
              Ferdig (demo)
            </button>
          </div>
        );
      }}
    </LevelRunnerShell>
  );
};

export default LevelPlayPage;
